import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { IconPicker } from '../ui/IconPicker.js';
import { renderCatalogIcon } from '../../utils/renderCatalogIcon.js';
import {
  Trash2,
  GripVertical,
  Pencil,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  Layers,
} from 'lucide-react';
import { ProductCategoryDef } from '../../api/types.js';
import {
  useCreateProductCategory,
  useUpdateProductCategory,
  useDeleteProductCategory,
} from '../../api/useProductCategories.js';

interface CategoryManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: ProductCategoryDef[];
  categoryCounts: Record<string, number>;
}

interface EditingCategory extends ProductCategoryDef {
  isEditing?: boolean;
}

export const CategoryManagerModal: React.FC<CategoryManagerModalProps> = ({
  isOpen,
  onClose,
  categories: initialCategories,
  categoryCounts,
}) => {
  const [categories, setCategories] = useState<EditingCategory[]>(initialCategories);
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('Package');
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EditingCategory | null>(null);
  const dragOverIndex = useRef<number | null>(null);

  const createMutation = useCreateProductCategory();
  const updateMutation = useUpdateProductCategory();
  const deleteMutation = useDeleteProductCategory();

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && mounted) {
      setCategories(initialCategories);
    }
  }, [isOpen, initialCategories, mounted]);

  const handleAddCategory = async () => {
    if (!newName.trim() || !newIcon.trim()) return;
    try {
      await createMutation.mutateAsync({
        name: newName.trim(),
        icon: newIcon.trim(),
        sortOrder: Math.max(...categories.map((c) => c.sortOrder), -1) + 1,
      });
      setNewName('');
      setNewIcon('Package');
    } catch (err: any) {
      toast.error(err?.message || err?.error || 'Errore creazione categoria');
    }
  };

  const handleEditStart = (id: string) => {
    setCategories((cats) =>
      cats.map((c) => (c.id === id ? { ...c, isEditing: true } : c))
    );
  };

  const handleEditEnd = async (id: string, name: string, icon: string) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat || (cat.name === name && cat.icon === icon)) {
      setCategories((cats) =>
        cats.map((c) => (c.id === id ? { ...c, isEditing: false } : c))
      );
      return;
    }

    try {
      await updateMutation.mutateAsync({ id, data: { name, icon } });
      setCategories((cats) =>
        cats.map((c) =>
          c.id === id ? { ...c, name, icon, isEditing: false } : c
        )
      );
    } catch (err: any) {
      toast.error(err?.message || err?.error || 'Errore aggiornamento categoria');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index <= 0) return;
    const newCategories = [...categories];
    const current = newCategories[index];
    const above = newCategories[index - 1];
    const tmpSort = current.sortOrder;
    current.sortOrder = above.sortOrder;
    above.sortOrder = tmpSort;
    [newCategories[index], newCategories[index - 1]] = [newCategories[index - 1], newCategories[index]];
    setCategories(newCategories);
    try {
      await Promise.all([
        updateMutation.mutateAsync({ id: current.id, data: { sortOrder: current.sortOrder } }),
        updateMutation.mutateAsync({ id: above.id, data: { sortOrder: above.sortOrder } }),
      ]);
    } catch (err: any) {
      toast.error(err?.message || err?.error || 'Errore ordinamento');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index >= categories.length - 1) return;
    const newCategories = [...categories];
    const current = newCategories[index];
    const below = newCategories[index + 1];
    const tmpSort = current.sortOrder;
    current.sortOrder = below.sortOrder;
    below.sortOrder = tmpSort;
    [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
    setCategories(newCategories);
    try {
      await Promise.all([
        updateMutation.mutateAsync({ id: current.id, data: { sortOrder: current.sortOrder } }),
        updateMutation.mutateAsync({ id: below.id, data: { sortOrder: below.sortOrder } }),
      ]);
    } catch (err: any) {
      toast.error(err?.message || err?.error || 'Errore ordinamento');
    }
  };

  const handleReorder = () => {
    if (draggedId === null || dragOverIndex.current === null) return;
    const draggedIndex = categories.findIndex((c) => c.id === draggedId);
    if (draggedIndex === dragOverIndex.current) {
      setDraggedId(null);
      dragOverIndex.current = null;
      return;
    }

    const newCategories = [...categories];
    const [draggedItem] = newCategories.splice(draggedIndex, 1);
    newCategories.splice(dragOverIndex.current, 0, draggedItem);

    const updatedWithOrder = newCategories.map((c, idx) => ({
      ...c,
      sortOrder: idx,
    }));

    setCategories(updatedWithOrder);

    updatedWithOrder.forEach((c) => {
      if (c.sortOrder !== initialCategories.find((ic) => ic.id === c.id)?.sortOrder) {
        updateMutation.mutateAsync({
          id: c.id,
          data: { sortOrder: c.sortOrder },
        }).catch((err: any) => toast.error(err?.message || err?.error || 'Errore ordinamento categoria'));
      }
    });

    setDraggedId(null);
    dragOverIndex.current = null;
  };

  const handleDragStart = (id: string) => {
    setDraggedId(id);
  };

  const handleDragOver = (index: number) => {
    dragOverIndex.current = index;
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setCategories((cats) => cats.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error(err?.message || err?.error || 'Errore eliminazione categoria');
    }
  };

  const isLoading = updateMutation.isPending || deleteMutation.isPending || createMutation.isPending;

  return (
    <>
      <style>{`
        .cm-overlay {
          position: fixed;
          inset: 0;
          z-index: 1000;
          background: oklch(0 0 0 / 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }
        .cm-panel {
          background: var(--glass-bg-card);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: var(--radius-container);
          width: 100%;
          max-width: 560px;
          max-height: 85dvh;
          display: flex;
          flex-direction: column;
          box-shadow: var(--shadow-lg);
          animation: cm-in 180ms cubic-bezier(0.16,1,0.3,1);
        }
        @keyframes cm-in {
          from { opacity: 0; transform: scale(0.96) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .cm-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 1.125rem 1.25rem;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          flex-shrink: 0;
        }
        .cm-header-left h3 {
          font-size: var(--text-md);
          font-weight: 600;
          color: var(--color-text);
          margin: 0 0 0.125rem 0;
        }
        .cm-header-left p {
          font-size: var(--text-xs);
          color: var(--color-text-muted);
          margin: 0;
        }
        .cm-count-badge {
          font-size: var(--text-xs);
          font-weight: 500;
          color: var(--color-text-muted);
          background: var(--glass-bg-card);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: var(--radius-full);
          padding: 0.2rem 0.625rem;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .cm-list {
          flex: 1;
          overflow-y: auto;
          padding: 0.75rem 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }
        .cm-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.625rem;
          border-radius: var(--radius-card);
          border: 1px solid transparent;
          background: transparent;
          transition: background var(--transition-interactive), border-color var(--transition-interactive), box-shadow var(--transition-interactive);
        }
        .cm-row:hover {
          background: var(--glass-bg-card);
        }
        .cm-row--dragging {
          opacity: 0.4;
          border-color: var(--color-primary);
          border-style: dashed;
          background: var(--glass-bg-card);
          box-shadow: var(--shadow-md);
        }
        .cm-drag-handle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          flex-shrink: 0;
          cursor: grab;
          color: var(--color-text-muted);
          transition: color var(--transition-interactive);
        }
        .cm-drag-handle:active {
          cursor: grabbing;
        }
        .cm-drag-handle:hover {
          color: var(--color-primary);
        }
        .cm-emoji-box {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--glass-bg-card);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: var(--radius-card);
          font-size: 1.125rem;
          flex-shrink: 0;
        }
        .cm-name {
          flex: 1;
          font-size: var(--text-sm);
          font-weight: 500;
          color: var(--color-text);
          min-width: 0;
          padding: 0.125rem 0.25rem;
        }
        .cm-name--editable {
          cursor: pointer;
          border-radius: var(--radius-interactive);
          transition: background var(--transition-interactive);
        }
        .cm-name--editable:hover {
          background: rgba(255,255,255,0.06);
        }
        .cm-product-count {
          font-size: var(--text-xs);
          color: var(--color-text-muted);
          background: rgba(255,255,255,0.06);
          border-radius: var(--radius-full);
          padding: 0.125rem 0.5rem;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .cm-actions {
          display: flex;
          align-items: center;
          gap: 0.125rem;
          flex-shrink: 0;
        }
        .cm-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: var(--radius-interactive);
          border: none;
          background: transparent;
          cursor: pointer;
          color: var(--color-text-muted);
          opacity: 0.5;
          transition: opacity var(--transition-interactive), background var(--transition-interactive), color var(--transition-interactive);
        }
        .cm-action-btn:hover {
          opacity: 1;
          background: rgba(255,255,255,0.06);
        }
        .cm-action-btn:focus-visible {
          opacity: 1;
          outline: 2px solid var(--color-primary);
          outline-offset: -2px;
        }
        .cm-action-btn--danger:hover {
          color: var(--color-error);
        }
        .cm-action-btn:disabled {
          opacity: 0.25;
          cursor: not-allowed;
        }
        .cm-action-btn:disabled:hover {
          color: var(--color-text-muted);
          background: transparent;
        }
        .cm-composer {
          padding: 0.75rem 1.25rem;
          border-top: 1px solid rgba(255,255,255,0.08);
          flex-shrink: 0;
        }
        .cm-composer-label {
          font-size: var(--text-xs);
          font-weight: 500;
          color: var(--color-text-muted);
          margin-bottom: 0.5rem;
        }
        .cm-composer-row {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .cm-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2.5rem 1rem;
          color: var(--color-text-muted);
          gap: 0.5rem;
        }
        .cm-empty p {
          font-size: var(--text-sm);
          margin: 0;
        }
        .cm-empty .cm-empty-hint {
          font-size: var(--text-xs);
          color: var(--color-text-faint);
        }
      `}</style>

      {isOpen && (
        <div
          className="cm-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        >
          <div className="cm-panel" role="dialog" aria-modal="true" aria-label="Categorie Prodotto">
            {/* Header */}
            <div className="cm-header">
              <div className="cm-header-left">
                <h3>Categorie Prodotto</h3>
                <p>Organizza, crea ed elimina le categorie del catalogo</p>
              </div>
              <span className="cm-count-badge">
                {categories.length} {categories.length === 1 ? 'categoria' : 'categorie'}
              </span>
            </div>

            {/* Category List */}
            <div className="cm-list">
              {categories.length === 0 ? (
                <div className="cm-empty">
                  <Layers size={32} strokeWidth={1.5} />
                  <p>Nessuna categoria</p>
                  <p className="cm-empty-hint">Crea la prima categoria usando il form qui sotto</p>
                </div>
              ) : (
                categories.map((cat, index) => {
                  const count = categoryCounts[cat.id] ?? 0;
                  const isFirst = index === 0;
                  const isLast = index === categories.length - 1;

                  return (
                    <CategoryRow
                      key={cat.id}
                      category={cat}
                      index={index}
                      productCount={count}
                      isFirst={isFirst}
                      isLast={isLast}
                      isDragging={draggedId === cat.id}
                      isLoading={isLoading}
                      onDragStart={() => handleDragStart(cat.id)}
                      onDragOver={() => handleDragOver(index)}
                      onDragEnd={handleReorder}
                      onEditStart={() => handleEditStart(cat.id)}
                      onEditEnd={(name, icon) => handleEditEnd(cat.id, name, icon)}
                      onMoveUp={() => handleMoveUp(index)}
                      onMoveDown={() => handleMoveDown(index)}
                      onDelete={
                        count > 0
                          ? undefined
                          : () => setDeleteTarget(cat)
                      }
                    />
                  );
                })
              )}
            </div>

            {/* Composer — Sticky Bottom */}
            <div className="cm-composer">
              <div className="cm-composer-label">Nuova categoria</div>
              <div className="cm-composer-row">
                <IconPicker value={newIcon} onChange={setNewIcon} />
                <Input
                  type="text"
                  placeholder="Nome categoria"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCategory();
                  }}
                  style={{ flex: 1 }}
                />
                <Button
                  onClick={handleAddCategory}
                  disabled={!newName.trim() || !newIcon.trim() || createMutation.isPending}
                  size="sm"
                  style={{ flexShrink: 0 }}
                >
                  Aggiungi
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Elimina categoria"
        description={
          deleteTarget
            ? `Eliminare la categoria "${deleteTarget.name}"? L'operazione e' irreversibile.`
            : ''
        }
        confirmLabel="Elimina categoria"
        loading={deleteMutation.isPending}
        requiresConfirmCheck
      />
    </>
  );
};

/* ─── CategoryRow ─────────────────────────────────────────────────── */

interface CategoryRowProps {
  category: ProductCategoryDef & { isEditing?: boolean };
  index: number;
  productCount: number;
  isFirst: boolean;
  isLast: boolean;
  isDragging: boolean;
  isLoading: boolean;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragEnd: () => void;
  onEditStart: () => void;
  onEditEnd: (name: string, icon: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: (() => void) | undefined;
}

const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  productCount,
  isFirst,
  isLast,
  isDragging,
  isLoading,
  onDragStart,
  onDragOver,
  onDragEnd,
  onEditStart,
  onEditEnd,
  onMoveUp,
  onMoveDown,
  onDelete,
}) => {
  const [editName, setEditName] = useState(category.name);
  const [editIcon, setEditIcon] = useState(category.icon);

  // Editing state
  if (category.isEditing) {
    return (
      <div className="cm-row">
        <div className="cm-drag-handle">
          <GripVertical size={15} />
        </div>
        <IconPicker value={editIcon} onChange={setEditIcon} />
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') onEditEnd(editName, editIcon);
            if (e.key === 'Escape') {
              setEditName(category.name);
              setEditIcon(category.icon);
            }
          }}
          style={{
            flex: 1,
            background: 'var(--glass-bg-card)',
            border: '1px solid var(--color-primary)',
            borderRadius: 'var(--radius-interactive)',
            color: 'var(--color-text)',
            fontSize: 'var(--text-sm)',
            padding: '0.25rem 0.5rem',
            outline: 'none',
          }}
        />
        <div className="cm-actions">
          <button
            className="cm-action-btn"
            onClick={() => onEditEnd(editName, editIcon)}
            disabled={isLoading}
            style={{ opacity: 1, color: 'var(--color-success)' }}
            title="Salva"
          >
            <Check size={14} />
          </button>
          <button
            className="cm-action-btn"
            onClick={() => {
              setEditName(category.name);
              setEditIcon(category.icon);
            }}
            style={{ opacity: 1 }}
            title="Annulla"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Normal state
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDrop={onDragEnd}
      className={`cm-row${isDragging ? ' cm-row--dragging' : ''}`}
    >
      {/* Drag handle */}
      <div className="cm-drag-handle" title="Trascina per riordinare">
        <GripVertical size={15} />
      </div>

      {/* Icon box */}
      <div className="cm-emoji-box">
        {renderCatalogIcon(category.icon, 18)}
      </div>

      {/* Name — click to edit */}
      <div
        className="cm-name cm-name--editable"
        onClick={onEditStart}
        title="Clicca per modificare"
      >
        {category.name}
      </div>

      {/* Product count */}
      {productCount > 0 && (
        <span className="cm-product-count" title={`Usata da ${productCount} prodotti`}>
          {productCount} {productCount === 1 ? 'prodotto' : 'prodotti'}
        </span>
      )}

      {/* Move up/down fallback */}
      <div className="cm-actions">
        <button
          className="cm-action-btn"
          onClick={onMoveUp}
          disabled={isFirst || isLoading}
          title="Sposta su"
        >
          <ChevronUp size={14} />
        </button>
        <button
          className="cm-action-btn"
          onClick={onMoveDown}
          disabled={isLast || isLoading}
          title="Sposta giu'"
        >
          <ChevronDown size={14} />
        </button>

        {/* Edit */}
        <button
          className="cm-action-btn"
          onClick={onEditStart}
          disabled={isLoading}
          title="Modifica nome o emoji"
        >
          <Pencil size={13} />
        </button>

        {/* Delete */}
        <button
          className={`cm-action-btn${onDelete ? ' cm-action-btn--danger' : ''}`}
          onClick={onDelete}
          disabled={!onDelete || isLoading}
          title={
            onDelete
              ? 'Elimina categoria'
              : `Usata da ${productCount} prodotti`
          }
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
};
